import React, { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = guest, object = user
  const [autoLogout, setAutoLogout] = useState(true);
  const [timeouts, setTimeouts] = useState(null);

  useEffect(() => {
    api.get("/features").then((r) => {
      setAutoLogout(r.data?.features?.auto_logout !== false);
      setTimeouts(r.data?.session_timeouts || null);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("mp_token");
    if (!token) { setUser(false); return; }
    api.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => { localStorage.removeItem("mp_token"); setUser(false); });
  }, []);

  const login = async (email, password, portal) => {
    const { data } = await api.post("/auth/login", { email, password, portal });
    localStorage.setItem("mp_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    localStorage.setItem("mp_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const setRole = async (role) => {
    const { data } = await api.put("/auth/role", { role });
    setUser(data);
    return data;
  };

  const logout = () => {
    // Fully clear the local session so the next login always starts fresh
    try {
      Object.keys(localStorage).filter((k) => k.startsWith("mp_")).forEach((k) => localStorage.removeItem(k));
      sessionStorage.clear();
    } catch (e) { /* ignore storage errors */ }
    setUser(false);
  };

  // Auto-logout after inactivity: admin/sub-admin 30 min, broker/owner/customer 60 min.
  // Gated by the "auto_logout" Feature Control toggle.
  useEffect(() => {
    if (!user || typeof user !== "object" || !autoLogout) return;
    const isAdmin = user.role === "admin" || user.role === "subadmin";
    const DEFAULT_T = { admin: 30, subadmin: 30, broker: 60, owner: 60, customer: 60 };
    const mins = (timeouts && Number(timeouts[user.role])) || DEFAULT_T[user.role] || 60;
    const limitMs = mins * 60 * 1000;
    let timer;
    const onTimeout = () => {
      logout();
      try { sessionStorage.setItem("mp_session_expired", "1"); } catch (e) { /* ignore */ }
      window.location.href = isAdmin ? "/admin/login" : "/login";
    };
    const reset = () => { clearTimeout(timer); timer = setTimeout(onTimeout, limitMs); };
    const events = ["mousemove", "keydown", "mousedown", "scroll", "touchstart"];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => { clearTimeout(timer); events.forEach((e) => window.removeEventListener(e, reset)); };
  }, [user, autoLogout, timeouts]);

  return (
    <AuthContext.Provider value={{ user, setUser, login, register, setRole, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
